import {
  activeHomepageHeroSlides,
  DEFAULT_HERO_INTERVAL_MS,
  mergeHomepageHeroConfig,
  type HomepageHeroConfig,
  type HomepageHeroSlideConfig,
} from '@shopgnd/shared';
import type { CarouselSlide } from '@/components/HeroCarousel';
import { IMAGE_DIMENSIONS, optimizeRemoteImageUrl } from '@/lib/imageUrl';
import {
  MARKETPLACE_HERO_SUB,
  MARKETPLACE_HERO_TAGLINE,
  MARKETPLACE_TAGLINE,
} from '@/lib/seo/constants';

/** Default hero src width (srcset supplies smaller variants). */
const HERO_IMAGE_WIDTH = IMAGE_DIMENSIONS.hero.width;

function isBrandSlide(slide: HomepageHeroSlideConfig): boolean {
  return slide.id === 'slide-brand' || slide.kind === 'brand';
}

/** Brand slide marketing copy is canonical — admin JSON cannot drift from static HTML / SEO constants. */
function normalizeBrandSlide(
  slide: HomepageHeroSlideConfig,
  defaultBrand: HomepageHeroSlideConfig | undefined,
): HomepageHeroSlideConfig {
  const base = defaultBrand ? { ...defaultBrand, ...slide } : slide;
  return {
    ...base,
    badge: defaultBrand?.badge ?? base.badge ?? 'ShopGND',
    headline: MARKETPLACE_TAGLINE,
    tagline: MARKETPLACE_HERO_TAGLINE,
    sub: MARKETPLACE_HERO_SUB,
  };
}

function normalizeHeroSlides(
  slides: HomepageHeroSlideConfig[],
  defaults: HomepageHeroConfig,
): HomepageHeroSlideConfig[] {
  const defaultBrand = defaults.slides.find(isBrandSlide);
  return slides.map((slide) =>
    isBrandSlide(slide) ? normalizeBrandSlide(slide, defaultBrand) : slide,
  );
}

function brandSlideCopyKey(config: HomepageHeroConfig): string {
  const brand = config.slides.find(isBrandSlide);
  if (!brand) return '';
  return [brand.headline, brand.tagline, brand.sub].join('\0');
}

export function normalizeHomepageHeroConfig(config: HomepageHeroConfig): HomepageHeroConfig {
  const defaults = buildDefaultHomepageHeroConfig();
  return {
    ...config,
    slides: normalizeHeroSlides(config.slides ?? [], defaults),
  };
}

/** Built-in slides when admin has not saved custom hero config. */
export function buildDefaultHomepageHeroConfig(): HomepageHeroConfig {
  return {
    intervalMs: DEFAULT_HERO_INTERVAL_MS,
    slides: [
      {
        id: 'slide-brand',
        kind: 'brand',
        sortOrder: 0,
        badge: 'ShopGND',
        headline: MARKETPLACE_TAGLINE,
        tagline: MARKETPLACE_HERO_TAGLINE,
        sub: MARKETPLACE_HERO_SUB,
        ctaLabel: 'Start shopping',
        ctaTo: '/grenada/shop',
        includeCategoryPills: false,
        pills: [
          { label: 'Shop', to: '/grenada/shop' },
          { label: 'Property', to: '/grenada/real-estate' },
          { label: 'Classifieds', to: '/grenada/classifieds' },
          { label: 'Find a Pro', to: '/grenada/find-a-pro', accent: true },
        ],
      },
      {
        id: 'slide-shop',
        kind: 'image',
        sortOrder: 1,
        imageUrl: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d',
        overlayColor: 'from-brand-900/80 via-brand-900/50 to-transparent',
        badge: 'Featured vendors',
        headline: 'Shop the best of Grenada',
        sub: 'Electronics, fashion, home goods & more — from trusted local vendors across Grenada, Carriacou, and Petite Martinique.',
        ctaLabel: 'Browse all products',
        ctaTo: '/grenada/shop',
      },
      {
        id: 'slide-electronics',
        kind: 'image',
        sortOrder: 2,
        imageUrl: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1',
        overlayColor: 'from-slate-900/80 via-slate-900/50 to-transparent',
        badge: 'Tech & gadgets',
        headline: 'Electronics for island life',
        sub: 'TVs, phones, chargers, and audio — from trusted Grenadian vendors with island-wide delivery.',
        ctaLabel: 'Shop electronics',
        ctaTo: '/grenada/shop?category=electronics',
      },
      {
        id: 'slide-fashion',
        kind: 'image',
        sortOrder: 3,
        imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8',
        overlayColor: 'from-slate-900/80 via-slate-900/50 to-transparent',
        badge: 'New arrivals',
        headline: 'Fresh styles, great prices',
        sub: 'Discover the latest fashion from Grenadian boutiques and independent sellers.',
        ctaLabel: 'Shop fashion',
        ctaTo: '/grenada/shop?category=fashion',
      },
      {
        id: 'slide-boost',
        kind: 'image',
        sortOrder: 4,
        imageUrl: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43',
        overlayColor: 'from-brand-900/85 via-brand-900/60 to-transparent',
        badge: 'Advertise with us',
        headline: 'Put your business in front of thousands',
        sub: 'Reach Grenadian shoppers daily. Book a featured slot and grow your sales on ShopGND.',
        ctaLabel: 'Get featured',
        ctaTo: '/boost',
      },
      {
        id: 'slide-pro',
        kind: 'image',
        sortOrder: 5,
        imageUrl: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e',
        overlayColor: 'from-slate-900/80 via-slate-900/50 to-transparent',
        badge: 'Find a Pro',
        headline: 'Hire trusted professionals',
        sub: 'Electricians, plumbers, AC techs and more — vetted service providers across every location.',
        ctaLabel: 'Find a Pro',
        ctaTo: '/grenada/find-a-pro',
      },
    ],
  };
}

let cachedHero: HomepageHeroConfig = buildDefaultHomepageHeroConfig();
const heroListeners = new Set<() => void>();

export function subscribeHomepageHero(listener: () => void): () => void {
  heroListeners.add(listener);
  return () => {
    heroListeners.delete(listener);
  };
}

export function applyHomepageHero(config: HomepageHeroConfig): void {
  const normalized = normalizeHomepageHeroConfig(config);
  const prevCopy = brandSlideCopyKey(cachedHero);
  const nextCopy = brandSlideCopyKey(normalized);
  cachedHero = normalized;
  if (prevCopy !== nextCopy) {
    for (const listener of heroListeners) listener();
  }
}

export function getRuntimeHomepageHero(): HomepageHeroConfig {
  return cachedHero;
}

export function parseHomepageHeroJson(raw: string | undefined | null): HomepageHeroConfig {
  const defaults = buildDefaultHomepageHeroConfig();
  if (!raw?.trim()) return defaults;
  try {
    const parsed = JSON.parse(raw) as Partial<HomepageHeroConfig>;
    if (!parsed.slides || !Array.isArray(parsed.slides) || parsed.slides.length === 0) {
      return defaults;
    }
    return normalizeHomepageHeroConfig(mergeHomepageHeroConfig(parsed, defaults));
  } catch {
    return defaults;
  }
}

export function serializeHomepageHero(config: HomepageHeroConfig): string {
  return JSON.stringify(normalizeHomepageHeroConfig(config), null, 2);
}

export function heroConfigToCarouselSlides(
  config: HomepageHeroConfig,
  options?: {
    categoryPills?: CarouselSlide['pills'];
  },
): CarouselSlide[] {
  return activeHomepageHeroSlides(config.slides ?? []).map((slide) => configSlideToCarousel(slide, options));
}

function configSlideToCarousel(
  slide: HomepageHeroSlideConfig,
  options?: { categoryPills?: CarouselSlide['pills'] },
): CarouselSlide {
  const kind = slide.kind ?? (slide.id === 'slide-brand' ? 'brand' : slide.imageUrl ? 'image' : 'solid');
  const pills = [...(slide.pills ?? [])];
  if (kind === 'brand' && slide.includeCategoryPills !== false && options?.categoryPills?.length) {
    const maxPills = 6;
    const room = Math.max(0, maxPills - pills.length);
    if (room > 0) pills.push(...options.categoryPills.slice(0, room));
  }

  const image =
    kind === 'image' && slide.imageUrl
      ? optimizeRemoteImageUrl(slide.imageUrl, HERO_IMAGE_WIDTH)
      : undefined;

  return {
    id: slide.id,
    layout: kind === 'brand' ? 'brand' : 'standard',
    image,
    bgClass: kind === 'solid' || kind === 'brand' ? slide.bgClass ?? 'bg-brand-900' : slide.bgClass,
    overlayColor: slide.overlayColor,
    badge: slide.badge,
    headline: slide.headline,
    tagline: slide.tagline,
    sub: slide.sub ?? '',
    ctaLabel: slide.ctaLabel,
    ctaTo: slide.ctaTo,
    pills: pills.length ? pills : undefined,
  };
}

export function newHeroSlideId(): string {
  return `slide-${Date.now().toString(36)}`;
}
